import React from 'react'
import { Link as GatsbyLink } from 'gatsby'
import { ScrollSmoother } from '@dboxdev/ui-components/gsap'
import { DownloadIcon } from 'components/elements/icons'

// Icons selectable via the link's `Icon` enum in Strapi, shown after the label.
const ICONS = {
  Download: DownloadIcon
}

// Normalises a path for comparison: strips trailing slashes and treats an empty
// path as the root, so '/', '' and '/home/' compare consistently.
const normalizePath = (path) => (path || '/').replace(/\/+$/, '') || '/'

// Renders a Strapi `elements.link` component, choosing the right element based
// on its Type (Internal/External/File/Section). Accepts `className` so it can be
// wrapped by styled-components, and optional `children` to override the label.
const Link = ({ data, className, children, ...props }) => {
  if (!data) return null

  const { Title, Type, Page, URL, File, SectionId, Icon, IconPosition } = data
  const IconComponent = ICONS[Icon]
  const icon = IconComponent ? <IconComponent /> : null
  const label = (
    <>
      {IconPosition === 'Left' && icon}
      {children || Title}
      {IconPosition !== 'Left' && icon}
    </>
  )

  switch (Type) {
    case 'External':
      return (
        <a href={URL} className={className} target='_blank' rel='noopener noreferrer' {...props}>
          {label}
        </a>
      )
    case 'File':
      return (
        <a href={File?.localFile?.publicURL} className={className} target='_blank' rel='noopener noreferrer' {...props}>
          {label}
        </a>
      )
    case 'Section':
      return (
        <a href={`#${SectionId}`} className={className} {...props}>
          {label}
        </a>
      )
    case 'Internal':
    default: {
      const to = Page?.Path || '/'
      // A link to the page we're already on must not trigger a Gatsby route
      // change: re-navigating to the same path tears down and recreates the
      // layout's ScrollSmoother (normalizeScroll), which leaves the page locked
      // and unscrollable. Instead just smooth-scroll back to the top.
      const handleClick = (event) => {
        if (
          typeof window !== 'undefined' &&
          normalizePath(to) === normalizePath(window.location.pathname)
        ) {
          event.preventDefault()
          const smoother = ScrollSmoother.get?.()
          if (smoother) smoother.scrollTo(0, true)
          else window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        props.onClick?.(event)
      }

      return (
        <GatsbyLink to={to} className={className} {...props} onClick={handleClick}>
          {label}
        </GatsbyLink>
      )
    }
  }
}

export default Link
